/**
 * Central date/time standard for Compliance Hub (frontend).
 *
 * Single source of truth for parsing, storage serialisation and display of all
 * checklist date/time fields. Everything is anchored to the organisation
 * timezone (currently hard-defaulted to Australia/Sydney, DST-aware) so a value
 * entered in the field round-trips EXACTLY, regardless of the device timezone.
 *
 *  - datetime answers are STORED as ISO 8601 with the org-TZ offset,
 *    e.g. "2026-03-12T16:30:00+11:00"  (a valid instant AND the exact wall-clock)
 *  - date-only answers are STORED as a plain calendar date "YYYY-MM-DD"
 *    (never timezone-shifted)
 */
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// TODO: source from the organisation setting when multi-timezone support lands.
export const ORG_TZ = "Australia/Sydney";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const hasZone = (s) => /[zZ]$|[+-]\d{2}:?\d{2}$/.test(String(s));

// Parse a stored value into a dayjs anchored to the org timezone.
const toOrg = (stored) => {
  const s = String(stored);
  return hasZone(s) ? dayjs(s).tz(ORG_TZ) : dayjs.tz(s, ORG_TZ);
};

/** Stored value -> value for a native <input type="datetime-local" | "date">. */
export function toInput(stored, kind = "datetime") {
  if (!stored) return "";
  if (kind === "date") return String(stored).slice(0, 10);
  const d = toOrg(stored);
  return d.isValid() ? d.format("YYYY-MM-DDTHH:mm") : "";
}

/** Native input value -> canonical stored value. */
export function fromInput(input, kind = "datetime") {
  if (!input) return "";
  if (kind === "date") return String(input).slice(0, 10);
  const d = dayjs.tz(String(input), ORG_TZ); // interpret the wall-clock in org TZ
  return d.isValid() ? d.format() : ""; // e.g. 2026-03-12T16:30:00+11:00
}

/** Display a datetime as "DD/MM/YYYY — h:mm AM/PM" in the org timezone. */
export function fmtDateTime(stored) {
  if (!stored) return "";
  const d = toOrg(stored);
  return d.isValid() ? d.format("DD/MM/YYYY — h:mm A") : "";
}

/** Display a date as "DD/MM/YYYY" (date-only values are never shifted). */
export function fmtDate(stored) {
  if (!stored) return "";
  const s = String(stored);
  if (DATE_ONLY.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }
  const d = toOrg(s);
  return d.isValid() ? d.format("DD/MM/YYYY") : "";
}
